import { resolveConditionExpression, log } from '@mkt-client/infra-utils';
import { validateConfig, calculateWeightedRandom } from './utils.js';
import { ExperimentEvaluationError } from './errors.js';
/**
 * Class to handle experiment arm selection and condition evaluation.
 */
export class ExperimentQualifier {
    /**
     * Constructor to initialize the ExperimentQualifier.
     * @param {Config} config - The configuration for experiments and arms.
     * @param {IRequestAdapter} requestAdapter - The request adapter for environment-specific operations.
     */
    constructor(config, requestAdapter) {
        validateConfig(config.experiments, config.experiment_arms);
        this.config = config;
        this.requestAdapter = requestAdapter;
    }
    /**
     * Extracts experiment info from storage/cookies
     * @returns {ExperimentArms} Parsed experiment info or empty object if none found
     * @private
     */
    getStoredExperimentInfo() {
        return this.requestAdapter.getStoredExperiments();
    }
    /**
     * Extracts forced arms from storage/cookies and query parameters.
     * @returns {{
     *   forcedArmsFromQuery: Set<string>;
     *   forcedArmsFromCookie: Set<string>;
     * }} - Sets containing forced arm IDs from query parameters and storage/cookies.
     */
    extractForcedArms() {
        const forcedArmsFromCookie = new Set();
        const forcedArmsFromQuery = new Set();
        // Extract from storage/cookies
        const experimentsFromStorage = this.getStoredExperimentInfo();
        // Extract experiment arms from stored info
        if (experimentsFromStorage) {
            for (const [experimentId, experiment] of Object.entries(experimentsFromStorage)) {
                if (experiment?.id) {
                    forcedArmsFromCookie.add(experiment.id);
                }
            }
        }
        // Extract from query parameters
        const urlParams = this.requestAdapter.getQueryParams();
        for (const armId of urlParams.getAll('arm')) {
            if (armId) {
                forcedArmsFromQuery.add(armId);
            }
        }
        return { forcedArmsFromQuery, forcedArmsFromCookie };
    }
    /**
     * Selects the appropriate experiment arms based on forced assignments and weights.
     * @returns {SelectExperimentArmsResult} - The result of selecting experiment arms.
     */
    selectExperimentArms() {
        const forcedArmsFromQuery = new Set();
        const urlParams = this.requestAdapter.getQueryParams();
        for (const armId of urlParams.getAll('arm')) {
            if (armId) {
                forcedArmsFromQuery.add(armId);
            }
        }
        const requestUri = this.requestAdapter.getRequestUri();
        const storedExperiments = this.getStoredExperimentInfo();
        if (storedExperiments === null) {
            return {
                first_assignment: true,
                disallowed: false
            };
        }
        const selectedArms = this.selectArms(forcedArmsFromQuery);
        const { disallowed, reason } = this.handleArmDisallowList(selectedArms, requestUri);
        const first_assignment = Object.keys(storedExperiments).length === 0;
        return {
            ...selectedArms,
            first_assignment,
            disallowed: disallowed || false,
            reason
        };
    }
    /**
     * Selects arms for all experiments based on forced assignments and weights.
     *
     * @param {Set<string>} forcedArmsFromQuery - Set of arm IDs forced via query parameters
     * @returns {ExperimentArms} Map of selected arms keyed by experiment ID
     */
    selectArms(forcedArmsFromQuery) {
        const selectedArms = {};
        const storedExperiments = this.getStoredExperimentInfo();
        if (storedExperiments === null) {
            return {};
        }
        for (const experiment of this.config.experiments) {
            const experimentId = experiment.id;
            const experimentArms = this.config.experiment_arms[experimentId] || [];
            if (experimentArms.length === 0)
                continue;
            let selectedConfigArm;
            // Priority order:
            // 1. Query parameters (for testing)
            if (forcedArmsFromQuery.size > 0) {
                selectedConfigArm = experimentArms.find(arm => forcedArmsFromQuery.has(arm.id));
            }
            // 2. Stored experiments (from adapter)
            if (!selectedConfigArm && storedExperiments[experimentId]?.id) {
                const storedArmId = storedExperiments[experimentId].id;
                const storedArmStillExists = experimentArms.some(arm => arm.id === storedArmId);
                if (storedArmStillExists) {
                    selectedConfigArm = experimentArms.find(arm => arm.id === storedArmId);
                }
            }
            // 3. Random selection (only for new assignments)
            if (!selectedConfigArm) {
                selectedConfigArm = calculateWeightedRandom(experimentArms);
            }
            if (selectedConfigArm) {
                selectedArms[experimentId] = {
                    id: selectedConfigArm.id,
                    condition_expression: experiment.condition_expression,
                    meta: selectedConfigArm.meta
                };
            }
        }
        return selectedArms;
    }
    /**
     * Retrieves the disallow list for a specific arm in an experiment.
     *
     * @param {Experiment} experiment - The experiment configuration
     * @param {string} armId - ID of the arm to get disallow list for
     * @returns {string[]} Array of URI paths where this arm is disallowed
     */
    getArmDisallowList(experiment, armId) {
        if (!experiment.meta)
            return [];
        const armDisallowListEntries = experiment.meta
            .filter((m) => m.type === 'arm_disallow_list')
            .flatMap((m) => m.value);
        const entry = armDisallowListEntries.find(entry => entry.id === armId);
        return entry?.disallow_list || [];
    }
    /**
     * Finds an alternative arm for an experiment, excluding the current arm.
     *
     * @param {ConfigExperimentArm[]} arms - Available arms for the experiment
     * @param {string} currentArmId - ID of the current arm to exclude
     * @returns {ConfigExperimentArm | undefined} Alternative arm or undefined if none found
     */
    findAlternativeArm(arms, currentArmId) {
        return arms.find(arm => arm.id !== currentArmId);
    }
    /**
     * Handles disallow list logic by potentially switching arms if the selected arm is disallowed for the requested URI.
     */
    handleArmDisallowList(selectedArms, requestUri) {
        let disallowed = false;
        let reason;
        for (const [experimentId, selectedArm] of Object.entries(selectedArms)) {
            if (!selectedArm?.id)
                continue;
            const experiment = this.config.experiments.find(exp => exp.id === experimentId);
            if (!experiment)
                continue;
            const disallowList = this.getArmDisallowList(experiment, selectedArm.id);
            const isDisallowed = disallowList.some(path => requestUri.includes(path));
            if (isDisallowed) {
                const configArms = this.config.experiment_arms[experimentId];
                if (!configArms)
                    continue;
                const alternativeArm = this.findAlternativeArm(configArms, selectedArm.id);
                if (alternativeArm) {
                    selectedArms[experimentId] = {
                        id: alternativeArm.id,
                        condition_expression: selectedArm.condition_expression,
                        meta: alternativeArm.meta
                    };
                }
                disallowed = true;
                reason = alternativeArm
                    ? `Arm '${selectedArm.id}' disallowed for URI '${requestUri}'. Switched to arm '${alternativeArm.id}'.`
                    : `Arm '${selectedArm.id}' disallowed for URI '${requestUri}', and no alternative arm was found.`;
            }
        }
        return { disallowed, reason };
    }
    /**
     * Static method to evaluate conditions from the header and disqualify arms if necessary.
     * @param {object} experiments - The experiments object.
     * @param {RequestContext} requestContext - The request context.
     * @returns {object} - The updated experiments object after condition evaluation.
     */
    static evaluateConditionFromHeader(experiments, requestContext) {
        try {
            const updatedExperiments = { ...experiments };
            for (const [experimentId, experiment] of Object.entries(experiments)) {
                const { condition_expression } = experiment;
                try {
                    const conditionMet = resolveConditionExpression(condition_expression, requestContext);
                    if (!conditionMet) {
                        delete updatedExperiments[experimentId];
                    }
                }
                catch (error) {
                    log('ERROR', `Failed to evaluate condition for experiment ${experimentId}`, error);
                    delete updatedExperiments[experimentId];
                }
            }
            return updatedExperiments;
        }
        catch (error) {
            if (error instanceof Error) {
                throw new ExperimentEvaluationError(`Failed to process experiment header: ${error.message}`);
            }
            throw new ExperimentEvaluationError('Failed to process experiment header: Unknown error');
        }
    }
}
